import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import type { FieldExtractionResult } from '@nsc/field-extraction/entities/FieldExtractionResult.js';
import { html, nothing } from 'lit';
import { subscribe } from '../api/WorkflowAPI.js';

/**
 * Renders a field extraction result with extracted values and missing fields
 * @param extraction The field extraction result to render
 * @returns A lit-html template
 */
const renderFieldExtractionResult = (
  extraction:
    | NarrowedEntity<
        FieldExtractionResult,
        {
          createdAt: 1;
          missing: 1;
          invalid: 1;
          values: {
            key: 1;
            value: 1;
          };
        }
      >
    | null
    | undefined,
  diff?:
    | NarrowedEntity<
        FieldExtractionResult,
        {
          createdAt: 1;
          missing: 1;
          invalid: 1;
          values: {
            key: 1;
            value: 1;
          };
        }
      >
    | null
    | undefined,
) => {
  if (!extraction) {
    return html`<div class="text-sm text-neutral/70">No details available.</div>`;
  }

  return html`
    <div class="mb-2">
      <div class="text-sm text-neutral/70">Extracted at</div>
      <div>${extraction.createdAt ? new Date(extraction.createdAt).toLocaleString() : '-'}</div>
    </div>
    <div class="bg-base-200 rounded p-2 text-xs">
      ${extraction.values?.length
        ? html`
            <ul class="list-disc pl-4 space-y-1">
              ${extraction.values.map(value => {
                if (diff) {
                  if (!diff.missing?.includes(value.key ?? '') && !diff.invalid?.includes(value.key ?? '')) {
                    return nothing;
                  }
                }

                const invalid = extraction.isInvalid(value.key ?? '');
                const isMissing = extraction.isMissing(value.key ?? '');

                return html`
                  <li>
                    <span class="font-medium">${value.key}:</span> ${isMissing
                      ? html`<span class="text-red-500">MISSING</span>`
                      : invalid
                        ? html`<span class="text-red-500">${value.value} (INVALID)</span>`
                        : value.value}
                  </li>
                `;
              })}
            </ul>
          `
        : html`<div>No values extracted</div>`}
    </div>
  `;
};

const IntrospectionPage: ReturnType<ReturnType<typeof stateful>> = stateful(() => {
  const states = useObservable(
    () =>
      subscribe(
        0,
        1000,
        new AdornisFilter({}),
      )({
        type: 1,
        ticketID: 1,
        createdAt: 1,
        updatedAt: 1,
        dutyManagerCalled: 1,
        dutyManagerOrMailboxAnswered: 1,
        clientCalled: 1,
        clientOrMailboxAnswered: 1,
        error: 1,
        client: {
          name: 1,
        },
        initialExtraction: {
          input: 1,
          createdAt: 1,
          missing: 1,
          invalid: 1,
          values: {
            key: 1,
            value: 1,
          },
        },
        updatedExtractions: {
          input: 1,
          createdAt: 1,
          missing: 1,
          invalid: 1,
          values: {
            key: 1,
            value: 1,
          },
        },
      }),
    [],
  );

  return html`
    <div class="max-w-4xl mx-auto py-8">
      <h1 class="text-3xl font-bold mb-6 text-primary">Process Introspection</h1>
      ${(states ?? []).length === 0
        ? html` <div class="text-neutral/60 text-center mt-8">No processes found.</div> `
        : html`
            <div class="grid gap-6">
              ${(states ?? []).map(
                state => html`
                  <div class="card bg-base-100 shadow-md border border-base-200">
                    <div class="card-body">
                      <h2 class="card-title text-xl font-semibold text-neutral">
                        Enrichment: Ticket ${state.ticketID}
                      </h2>
                      <div class="grid grid-cols-2 gap-4 mt-2">
                        <div>
                          <div class="text-sm text-neutral/70">Status</div>
                          <div class="badge">${state.type}</div>
                        </div>
                        <div>
                          <div class="text-sm text-neutral/70">Started At</div>
                          <div>${state.createdAt ? new Date(state.createdAt).toLocaleString() : '-'}</div>
                        </div>
                        <div>
                          <div class="text-sm text-neutral/70">Last Updated</div>
                          <div>${state.updatedAt ? new Date(state.updatedAt).toLocaleString() : '-'}</div>
                        </div>
                        <div>
                          <div class="text-sm text-neutral/70">Owner</div>
                          <div>${state.client?.name}</div>
                        </div>
                        <div class="col-span-2 border border-base-300 p-4 rounded">
                          <div class="flex justify-between mb-2 pb-2 border-b border-base-300">
                            <div class="text-lg font-semibold text-neutral/70">Phone Calls</div>
                            ${state.error ? html`<div class="text-error text-sm">Error: ${state.error}</div>` : nothing}
                          </div>

                          <div class="grid grid-cols-2 gap-4">
                            <div>
                              <div class="text-sm text-neutral/70">Duty Manager</div>
                              <div class="flex items-center gap-2 mt-1">
                                <span class="badge ${state.dutyManagerCalled ? 'badge-primary' : ''}">
                                  ${state.dutyManagerCalled ? 'Called' : 'Not called'}
                                </span>
                                <span
                                  class="badge ${state.dutyManagerCalled
                                    ? state.dutyManagerOrMailboxAnswered
                                      ? 'badge-success'
                                      : 'badge-warning'
                                    : 'bade-success'}"
                                >
                                  ${state.dutyManagerCalled
                                    ? state.dutyManagerOrMailboxAnswered
                                      ? 'Answered or Mailbox'
                                      : 'Not Answered'
                                    : 'No Call Made'}
                                </span>
                              </div>
                            </div>

                            <div>
                              <div class="text-sm text-neutral/70">Client</div>
                              <div class="flex items-center gap-2 mt-1">
                                <span class="badge ${state.clientCalled ? 'badge-primary' : ''}">
                                  ${state.clientCalled ? 'Called' : 'Not called'}
                                </span>
                                <span
                                  class="badge ${state.clientCalled
                                    ? state.clientOrMailboxAnswered
                                      ? 'badge-success'
                                      : 'badge-warning'
                                    : 'bade-success'}"
                                >
                                  ${state.clientCalled
                                    ? state.clientOrMailboxAnswered
                                      ? 'Answered or Mailbox'
                                      : 'Failed'
                                    : 'No Call Made'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-span-2 border border-base-300 p-4 rounded">
                          <div class="flex justify-between mb-2 pb-2 border-b border-base-300">
                            <div class="text-lg font-semibold text-neutral/70">Initial Extraction</div>
                            <div class="text-sm text-neutral/70"></div>
                          </div>

                          ${renderFieldExtractionResult(state.initialExtraction)}
                        </div>

                        ${(state.updatedExtractions ?? []).map(
                          (extraction, i) => html`
                            <div class="col-span-2 border border-base-300 p-4 rounded">
                              <div class="flex justify-between mb-2 pb-2 border-b border-base-300">
                                <div class="text-lg font-semibold text-neutral/70">Updated Extraction ${i + 1}</div>
                                <div class="text-sm text-neutral/70"></div>
                              </div>

                              ${renderFieldExtractionResult(
                                extraction,
                                i === 0 ? state.initialExtraction : (state.updatedExtractions?.[i - 1] ?? null),
                              )}
                            </div>
                          `,
                        )}
                      </div>
                    </div>
                  </div>
                `,
              )}
            </div>
          `}
    </div>
  `;
});

export default IntrospectionPage;

// ${extraction.missingOnlyRequired().length
//               ? html`
//                   <ul class="list-disc pl-4 space-y-1 mt-2 border-t border-neutral-300 pt-2">
//                     <li>Missing Fields: ${extraction.missingOnlyRequired().join(', ')}</li>
//                   </ul>
//                 `
//               : nothing}
//             ${extraction.invalidOnlyRequired().length
//               ? html`
//                   <ul class="list-disc pl-4 space-y-1 mt-2 border-t border-neutral-300 pt-2">
//                     <li>Invalid Fields: ${extraction.invalidOnlyRequired().join(', ')}</li>
//                   </ul>
//                 `
//               : nothing}

//  ${state.initialExtraction?.missingOnlyRequired().length
//                                 ? html`<span class="text-red-600"
//                                     >Missing: ${state.initialExtraction?.missingOnlyRequired().join(', ')}</span
//                                   >`
//                                 : html`<span class="text-green-600">Nothing missing</span>`}
//                               •
//                               ${state.initialExtraction?.invalidOnlyRequired().length
//                                 ? html`<span class="text-red-600"
//                                     >Invalid: ${state.initialExtraction?.invalidOnlyRequired().join(', ')}</span
//                                   >`
//                                 : html`<span class="text-green-600">Nothing invalid</span>`}

// ${extraction.missingOnlyRequired().length
//   ? html`<span class="text-red-600"
//       >Missing: ${extraction.missingOnlyRequired().join(', ')}</span
//     >`
//   : html`<span class="text-green-600">Nothing missing</span>`}
// •
// ${extraction.invalidOnlyRequired().length
//   ? html`<span class="text-red-600"
//       >Invalid: ${extraction.invalidOnlyRequired().join(', ')}</span
//     >`
//   : html`<span class="text-green-600">Nothing invalid</span>`}
